import { stringsEs } from "./strings.es";
import { stringsEn } from "./strings.en";
import type { Language, Strings } from "./types";

export type { Language, Strings };

/**
 * Look up the string table for a language. Spanish is the source of truth;
 * unknown / undefined languages fall back to Spanish. There is no per-key
 * cross-language fallback in v1 — the TypeScript interface guarantees both
 * tables are complete, so a missing key would have failed the build.
 *
 * Sacred-name strings (Yahushua, Yahweh, Padre, Todopoderoso) are equal
 * across both tables on purpose. See strings.es.ts header for the rule.
 */
export function getStrings(language: Language): Strings {
  switch (language) {
    case "en":
      return stringsEn;
    case "es":
      return stringsEs;
    default: {
      const exhaustive: never = language;
      return stringsEs;
      // Reference the exhaustive check so TS keeps complaining if a future
      // Language variant is added without a case here. Unreachable at runtime.
      void exhaustive;
    }
  }
}

/**
 * Parse a language hint (e.g. "?lang=en" or browser `navigator.language`).
 * Anything that does not start with "en" maps to "es" — Spanish is the
 * default per docs/public-chat-architecture.md §8.
 */
export function resolveLanguage(hint: string | null | undefined): Language {
  if (typeof hint !== "string") return "es";
  return hint.toLowerCase().startsWith("en") ? "en" : "es";
}
